import React, { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { Shield, Users, Activity, Database, AlertTriangle, TrendingUp } from 'lucide-react'

interface AdminUser {
  id: string
  username: string
  email: string
  role: 'player' | 'moderator' | 'admin' | 'super_admin'
  is_banned: boolean
  ban_reason?: string
  created_at: string
  last_login_at?: string
  login_count: number
}

interface AdminLog {
  id: string
  admin_id: string
  target_user_id: string
  action: string
  details: any
  created_at: string
  admin?: { username: string }
  target_user?: { username: string }
}

const AdminDashboard = () => {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [logs, setLogs] = useState<AdminLog[]>([])
  const [stats, setStats] = useState({
    totalUsers: 0,
    bannedUsers: 0,
    admins: 0,
    recentSignups: 0
  })
  const [loading, setLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [showUserModal, setShowUserModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'logs'>('overview')

  useEffect(() => {
    checkAdminAccess()
  }, [])

  const checkAdminAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      window.location.href = '/login'
      return
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()

    if (!profile || !['admin', 'super_admin', 'moderator'].includes(profile.role)) {
      window.location.href = '/dashboard'
      return
    }

    setCurrentUser(profile)
    loadData()
  }

  const loadData = async () => {
    setLoading(true)
    await Promise.all([
      loadUsers(),
      loadLogs(),
      loadStats()
    ])
    setLoading(false)
  }

  const loadUsers = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error loading users:', error)
    } else {
      setUsers(data || [])
    }
  }

  const loadLogs = async () => {
    const { data, error } = await supabase
      .from('admin_logs')
      .select(`
        *,
        admin:admin_id(username),
        target_user:target_user_id(username)
      `)
      .order('created_at', { ascending: false })
      .limit(20)

    if (error) {
      console.error('Error loading logs:', error)
    } else {
      setLogs(data || [])
    }
  }

  const loadStats = async () => {
    const { data: allUsers } = await supabase
      .from('profiles')
      .select('*')

    if (allUsers) {
      const totalUsers = allUsers.length
      const bannedUsers = allUsers.filter(u => u.is_banned).length
      const admins = allUsers.filter(u => ['admin', 'super_admin', 'moderator'].includes(u.role)).length
      const recentSignups = allUsers.filter(u => 
        new Date(u.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      ).length

      setStats({ totalUsers, bannedUsers, admins, recentSignups })
    }
  }

  const updateUserRole = async (userId: string, newRole: string) => {
    const { error } = await supabase
      .from('profiles')
      .update({ role: newRole })
      .eq('id', userId)

    if (error) {
      alert('Error updating user role: ' + error.message)
    } else {
      await supabase.from('admin_logs').insert({
        admin_id: currentUser.id,
        target_user_id: userId,
        action: 'role_changed',
        details: { new_role: newRole }
      })

      loadData()
      alert('User role updated successfully!')
    }
  }

  const banUser = async (userId: string, reason: string) => {
    const { error } = await supabase
      .from('profiles')
      .update({
        is_banned: true,
        ban_reason: reason
      })
      .eq('id', userId)

    if (error) {
      alert('Error banning user: ' + error.message)
    } else {
      await supabase.from('admin_logs').insert({
        admin_id: currentUser.id,
        target_user_id: userId,
        action: 'user_banned',
        details: { reason }
      })

      loadData()
      setShowUserModal(false)
      alert('User banned successfully!')
    }
  }

  const unbanUser = async (userId: string) => {
    const { error } = await supabase
      .from('profiles')
      .update({
        is_banned: false,
        ban_reason: null
      })
      .eq('id', userId)

    if (error) {
      alert('Error unbanning user: ' + error.message)
    } else {
      await supabase.from('admin_logs').insert({
        admin_id: currentUser.id,
        target_user_id: userId,
        action: 'user_unbanned',
        details: {}
      })

      loadData()
      setShowUserModal(false)
      alert('User unbanned successfully!')
    }
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'super_admin': return 'bg-red-500 text-white'
      case 'admin': return 'bg-purple-500 text-white'
      case 'moderator': return 'bg-blue-500 text-white'
      default: return 'bg-gray-500 text-white'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white">Loading admin dashboard...</div>
      </div>
    )
  }

  if (!currentUser || !['admin', 'super_admin', 'moderator'].includes(currentUser.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-red-400">Access Denied - Admin privileges required</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 pt-16">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <Shield className="w-10 h-10 mr-3 text-red-400" />
          <div>
            <h1 className="text-4xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-gray-400">Welcome, {currentUser.username} ({currentUser.role})</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex mb-8 border-b border-gray-700">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'logs', label: 'Activity Logs', icon: Database }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as any)}
              className={`flex items-center px-6 py-3 border-b-2 transition-colors ${
                activeTab === id
                  ? 'border-purple-400 text-purple-400'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-blue-400" />
                  <TrendingUp className="w-4 h-4 text-green-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{stats.totalUsers}</div>
                <div className="text-gray-400">Total Users</div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <Activity className="w-8 h-8 text-green-400" />
                  <div className="text-sm text-green-400">{stats.recentSignups} this week</div>
                </div>
                <div className="text-3xl font-bold text-white mb-2">{stats.recentSignups}</div>
                <div className="text-gray-400">New Signups</div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <Shield className="w-8 h-8 text-purple-400" />
                  <TrendingUp className="w-4 h-4 text-green-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{stats.admins}</div>
                <div className="text-gray-400">Administrators</div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <AlertTriangle className="w-8 h-8 text-orange-400" />
                  <div className="text-sm text-orange-400">{stats.bannedUsers > 0 ? 'Active' : 'None'}</div>
                </div>
                <div className="text-3xl font-bold text-white mb-2">{stats.bannedUsers}</div>
                <div className="text-gray-400">Banned Users</div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Recent Admin Activity</h3>
              <div className="space-y-3">
                {logs.slice(0, 5).map((log) => (
                  <div key={log.id} className="p-3 bg-slate-700/30 rounded-lg">
                    <div className="text-sm text-white">
                      {log.admin?.username || 'System'} {log.action.replace('_', ' ')} {log.target_user?.username || 'user'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(log.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold text-white mb-4">User Management</h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {users.map((user) => (
                <div key={user.id} className="bg-gray-700/50 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{user.username}</span>
                      <span className={`px-2 py-1 rounded text-xs ${getRoleBadge(user.role)}`}>
                        {user.role}
                      </span>
                      {user.is_banned && (
                        <span className="px-2 py-1 rounded text-xs bg-red-500 text-white">BANNED</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-400">{user.email}</div>
                    <div className="text-xs text-gray-500">
                      Joined: {new Date(user.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedUser(user)
                      setShowUserModal(true)
                    }}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                  >
                    Manage
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold text-white mb-4">Admin Activity Logs</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {logs.map((log) => (
                <div key={log.id} className="bg-gray-700/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-white capitalize">
                      {log.action.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Admin: {log.admin?.username || 'System'} → Target: {log.target_user?.username || 'N/A'}
                  </div>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      Details: {JSON.stringify(log.details)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Management Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-white mb-4">
                Manage User: {selectedUser.username}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Role</label>
                  <select
                    value={selectedUser.role}
                    onChange={(e) => updateUserRole(selectedUser.id, e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                    disabled={currentUser.role !== 'super_admin' && selectedUser.role === 'super_admin'}
                  >
                    <option value="player">Player</option>
                    <option value="moderator">Moderator</option>
                    {(currentUser.role === 'super_admin' || currentUser.role === 'admin') && (
                      <option value="admin">Admin</option>
                    )}
                    {currentUser.role === 'super_admin' && (
                      <option value="super_admin">Super Admin</option>
                    )}
                  </select>
                </div>

                <div className="flex gap-3">
                  {selectedUser.is_banned ? (
                    <button
                      onClick={() => unbanUser(selectedUser.id)}
                      className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                    >
                      Unban User
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        const reason = prompt('Ban reason:')
                        if (reason) banUser(selectedUser.id, reason)
                      }}
                      className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                      Ban User
                    </button>
                  )}
                  
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard